import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import AutocompleteField from '../inputs/AutocompleteField';
import ClientTagChip from './ClientTagChip';
import { getClientManagementPath } from '../../utils/settingsForm';

export default function ClientTagPicker({
  tags = [],
  value = [],
  onChange,
  disabled = false,
  fullWidth = true,
  label,
  helperText,
  showSettingsLink = false,
  isManager = false,
}) {
  const { t } = useTranslation();

  const options = useMemo(
    () => (tags || []).map((tag) => ({
      ...tag,
      value: String(tag._id),
      label: tag.name,
    })),
    [tags],
  );

  const selectedIds = Array.isArray(value) ? value.map(String) : [];
  const noTagsDefined = options.length === 0;

  if (noTagsDefined) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('clients.tags.noTagsDefined')}
        </Typography>
        {showSettingsLink && isManager && (
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            <RouterLink to={getClientManagementPath('tags')}>
              {t('clients.tags.createInSettings')}
            </RouterLink>
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <AutocompleteField
        label={label || t('clients.tags.label')}
        options={options}
        value={selectedIds}
        onChange={(next) => onChange(Array.isArray(next) ? next.map(String) : [])}
        multiple
        fullWidth={fullWidth}
        disabled={disabled}
        placeholder={t('clients.tags.selectPlaceholder')}
        getOptionLabel={(opt) => opt.label || opt.name || ''}
        getOptionValue={(opt) => String(opt.value || opt._id)}
        isOptionEqualToValue={(opt, val) => String(opt.value || opt._id) === String(val.value || val._id || val)}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.value || option._id}>
            <ClientTagChip tag={option} />
          </Box>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <ClientTagChip
                key={tagProps.key}
                tag={option}
                onDelete={tagProps.onDelete}
              />
            );
          })
        }
        sx={{ width: fullWidth ? '100%' : undefined }}
      />
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
